import { InfoIcon } from '@/assets/icons/info-icons/info';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import FileUpload from '@/components/basicComponents/fileUpload';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import Label from '@/components/basicComponents/label';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { SettingsContainer, SettingsSection } from '@/components/settings-page';
import { getCacheBustedFileUrl } from '@/components/settings-page/utils';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import axios from 'axios';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { fileExtensions } from '../constants';
import { IFirmSettings, IFontStyle } from '../types';

const getTemplateStylingLogoFilePath = (tenantId: string) => `logos/${tenantId}/template-styling-logo`;

interface EditLogoProps {
  currentRow: IFirmSettings | null;
  saveSettingValue: (key: string, value: string | IFontStyle) => Promise<void>;
  handleClose: () => void;
}

export const EditLogo = ({ currentRow, saveSettingValue, handleClose }: EditLogoProps) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [drag, setDrag] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'not-supported' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFileToDelete, setIsFileToDelete] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentRow) return;
    setFileUrl(currentRow?.value || '');
  }, [currentRow]);

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;
    setDrag(false);

    if (fileExtensions.includes(uploadFiles[0]?.name.split('.').pop()?.toUpperCase() ?? '')) {
      setFile(uploadFiles[0]);
      setFileUrl(URL.createObjectURL(uploadFiles[0]));
      return;
    }
    setModalWindow({ isOpen: true, type: 'not-supported' });
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;

    if (uploadFiles && fileExtensions.includes(uploadFiles[0]?.name.split('.').pop()?.toUpperCase() ?? '')) {
      setFile(uploadFiles[0]);
      setFileUrl(URL.createObjectURL(uploadFiles[0]));
      return;
    }
    setModalWindow({ isOpen: true, type: 'not-supported' });
  };

  const deletePreviewItem = () => {
    setFile(null);
    setFileUrl('');
    if (currentRow?.value) {
      setIsFileToDelete(true);
    }
  };

  const handleLogoUpload = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);

      if (isFileToDelete && !file) {
        const currentFileExtension = currentRow?.value?.split('.')?.pop()?.toLowerCase();

        await axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/unsecure-storage/file`, {
          data: {
            filePath: `${getTemplateStylingLogoFilePath(user?.tenant?.id)}.${currentFileExtension}`
          },
          headers: {
            authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        await saveSettingValue(currentRow?.key, '');
      }

      if (file) {
        const currentFileExtension = currentRow?.value?.split('.')?.pop()?.toLowerCase();
        const newFileExtension = file?.name?.split('.')?.pop()?.toLowerCase();
        if (currentRow?.value && currentFileExtension !== newFileExtension) {
          await axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/unsecure-storage/file`, {
            data: {
              filePath: `${getTemplateStylingLogoFilePath(user?.tenant?.id)}.${currentFileExtension}`
            },
            headers: {
              authorization: `Bearer ${localStorage.getItem('token')}`
            }
          });
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('filePath', `${getTemplateStylingLogoFilePath(user?.tenant?.id)}.${newFileExtension}`);
        formData.append('metadata', JSON.stringify({}));

        const { data } = await axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/unsecure-storage/file`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        await saveSettingValue(currentRow?.key, data?.url ?? '');
      }

      handleClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openLogoImage = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      {loading && (
        <ModalWrappedContent
          isOpen={loading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      {modalWindow.isOpen && modalWindow.type === 'not-supported' && (
        <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ ...modalWindow, isOpen: false })} />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="Templates" />} />
        <PageTitle title={currentRow?.fieldName ?? ''} />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <SettingsContainer>
          <SettingsSection isMobile={isMobile}>
            <Banner
              title=""
              description="Provide a logo that works well on a white or light colored background. The logo should have a horizontal orientation."
              icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
              bgColor={themeVar('layer.base')}
              color={themeVar('font.base')}
              dividerColor={themeVar('border.base')}
            />
            <EditLogoWrapper>
              <Label>{currentRow?.fieldName ?? ''}</Label>
              {fileUrl && (
                <FileUpload
                  fileName="Logo"
                  imageSrc={getCacheBustedFileUrl(fileUrl)}
                  type="image"
                  openFile={() => openLogoImage(getCacheBustedFileUrl(fileUrl))}
                  deleteFile={deletePreviewItem}
                />
              )}
              <DropFileArea
                id="firmLogo"
                fileExtensions={fileExtensions}
                handleUploadFile={handleUploadFile}
                onDropHandler={onDropHandler}
                drag={drag}
                setDrag={setDrag}
              />
            </EditLogoWrapper>
          </SettingsSection>
          <FormFooter onCancel={handleClose} onSave={handleLogoUpload} disableSaveButton={loading} showRequiredFieldsWarning={false} />
        </SettingsContainer>
      </MainWrap>
    </>
  );
};

const EditLogoWrapper = styled.div`
  padding-top: 30px;
`;
